/**
 * Central React Query keys and hooks.
 * Import KEYS for invalidation, import useXxx hooks in pages.
 *
 * staleTime overrides (per-query) override the global 2-min default.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

// ─── Query Keys ───────────────────────────────────────────────────────────────
export const KEYS = {
  properties:          ["properties"],
  propertyFinancials:  (id)     => ["properties", id, "financials"],
  rooms:               (propId) => ["rooms", propId],
  bookings:            (f = {}) => ["bookings", f],
  tasks:               (f = {}) => ["tasks", f],
  expenses:            (f = {}) => ["expenses", f],
  expensesSummary:     (f = {}) => ["expenses", "summary", f],
  dashboardStats:      ["dashboard", "stats"],
  dashboardComparison: ["dashboard", "comparison"],
  reviews:             ["reviews"],
  teamMembers:         ["team", "members"],
  messages:            ["messages"],
  pricingNudges:       ["pricing", "nudges"],
};

// ─── Properties ───────────────────────────────────────────────────────────────
export function useIntegrationSetup(userId) {
  return useQuery({
    queryKey: ["integration-setup", userId],
    enabled: !!userId,
    queryFn: async () => {
      const [feeds, properties, automations, configs] = await Promise.all([
        api.get("/ical/feeds").then(r => r.data),
        api.get("/properties").then(r => r.data),
        api.get("/automations").then(r => r.data),
        // Owner-only endpoint: staff get 403, which must not blank the whole page.
        api.get("/aiosell/config").then(r => r.data).catch(() => []),
      ]);
      return { feeds, properties, automations, configs };
    },
    refetchOnWindowFocus: false, // Returning from authorization must not overwrite a draft.
  });
}

export function useAirbnbRateSetup(userId) {
  return useQuery({
    queryKey: ["airbnb-rate-setup", userId],
    enabled: !!userId,
    queryFn: () => api.get("/airbnb-rates/setup").then(r => r.data),
    refetchOnWindowFocus: false,
  });
}

export function useProperties() {
  return useQuery({
    queryKey: KEYS.properties,
    queryFn:  () => api.get("/properties").then(r => r.data),
    staleTime: 0,
    refetchOnWindowFocus: true,          // auto-refresh when user switches back after Airbnb rate sync
    refetchInterval: 60 * 1000,          // light poll so dynamic-pricing rate changes surface on the calendar without a manual refresh
    refetchIntervalInBackground: false,  // only while the tab is visible — no background polling
  });
}

export function useRooms(propertyId, enabled = true) {
  return useQuery({
    queryKey: KEYS.rooms(propertyId),
    queryFn:  () => api.get(`/rooms?property_id=${propertyId}`).then(r => r.data),
    enabled:  !!propertyId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function usePropertyFinancials(id, enabled = true) {
  return useQuery({
    queryKey: KEYS.propertyFinancials(id),
    queryFn:  () => api.get(`/properties/${id}/financials`).then(r => r.data),
    enabled:  !!id && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Bookings ─────────────────────────────────────────────────────────────────
export function useBookings(filters = {}) {
  return useQuery({
    queryKey: KEYS.bookings(filters),
    queryFn:  () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && params.set(k, v));
      return api.get(`/bookings?${params}`).then(r => r.data);
    },
    staleTime: 60 * 1000,  // bookings can change — 1 min
  });
}

// ─── Tasks ────────────────────────────────────────────────────────────────────
export function useTasks(filters = {}) {
  return useQuery({
    queryKey: KEYS.tasks(filters),
    queryFn:  () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && params.set(k, v));
      return api.get(`/tasks?${params}`).then(r => r.data);
    },
    staleTime: 60 * 1000,
  });
}

// ─── Expenses ─────────────────────────────────────────────────────────────────
export function useExpenses(filters = {}) {
  return useQuery({
    queryKey: KEYS.expenses(filters),
    queryFn:  () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && params.set(k, v));
      return api.get(`/expenses?${params}`).then(r => r.data);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useExpensesSummary(filters = {}) {
  return useQuery({
    queryKey: KEYS.expensesSummary(filters),
    queryFn:  () => {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => v != null && params.set(k, v));
      return api.get(`/expenses/summary?${params}`).then(r => r.data);
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Dashboard ────────────────────────────────────────────────────────────────
export function useDashboardStats(period, selectedMonth, selectedYear, propertyId, roomId) {
  return useQuery({
    queryKey: [...KEYS.dashboardStats, period, selectedMonth, selectedYear, propertyId, roomId],
    queryFn:  () => {
      const params = new URLSearchParams();
      if (period)        params.set("period",         period);
      if (selectedMonth) params.set("selected_month", selectedMonth);
      if (selectedYear)  params.set("selected_year",  String(selectedYear));
      if (propertyId)    params.set("property_id",    propertyId);
      if (roomId)        params.set("room_id",        roomId);
      return api.get(`/dashboard/stats?${params}`).then(r => r.data);
    },
    staleTime: 2 * 60 * 1000,
  });
}

export function useDashboardComparison(period, month, year, propertyId) {
  return useQuery({
    queryKey: [...KEYS.dashboardComparison, period, month, year, propertyId],
    queryFn:  () => {
      const params = new URLSearchParams();
      if (period != null) params.set("period",          period);
      if (month  != null) params.set("selected_month",  month);
      if (year   != null) params.set("selected_year",   String(year));
      if (propertyId)     params.set("property_id",     propertyId);
      return api.get(`/dashboard/comparison?${params}`).then(r => r.data);
    },
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Reviews ──────────────────────────────────────────────────────────────────
export function useReviews() {
  return useQuery({
    queryKey: KEYS.reviews,
    queryFn:  () => api.get("/reviews").then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Messages / Inbox ─────────────────────────────────────────────────────────
export function useMessages() {
  return useQuery({
    queryKey: KEYS.messages,
    queryFn:  () => api.get("/messages").then(r => r.data),
    staleTime: 10 * 1000,
    // Poll so inbound WhatsApp messages appear without a manual refresh.
    // Only while the tab is visible — no background polling.
    refetchInterval: 12 * 1000,
    refetchIntervalInBackground: false,
  });
}

// Inbox nav badge. Keyed under "messages" so invalidating the inbox refreshes it too.
export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: [...KEYS.messages, "unread"],
    queryFn:  () => api.get("/messages/unread-count").then(r => r.data?.count || 0),
    enabled,
    refetchInterval: 30 * 1000,
    refetchIntervalInBackground: false,
  });
}

// Owner-scoped team overview; errors are surfaced instead of an empty member list.
export function useTeamAccess(ownerId) {
  return useQuery({
    queryKey: ["team", "access", ownerId],
    enabled: !!ownerId,
    queryFn: async () => {
      const [members, properties, defaults] = await Promise.all([
        api.get("/team/members").then(r => r.data),
        api.get("/properties").then(r => r.data),
        api.get("/team/role-defaults").then(r => r.data),
      ]);
      return { members, properties, defaults };
    },
    staleTime: 0,
  });
}

// ─── Team Members ─────────────────────────────────────────────────────────────
export function useTeamMembers() {
  return useQuery({
    queryKey: KEYS.teamMembers,
    queryFn:  () => api.get("/team/members").then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Channel-mix analytics (revenue/ADR/lead-time by OTA) ─────────────────────
export function useChannelMix() {
  return useQuery({
    queryKey: ["analytics", "channel-mix"],
    queryFn:  () => api.get("/analytics/channel-mix").then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Smart Pricing Alerts (nudges) ────────────────────────────────────────────
export function usePricingNudges() {
  return useQuery({
    queryKey: KEYS.pricingNudges,
    queryFn:  () => api.get("/pricing/nudges").then(r => r.data),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

// ─── Invalidation helpers ─────────────────────────────────────────────────────
export function useInvalidate() {
  const qc = useQueryClient();
  return {
    properties:      () => qc.invalidateQueries({ queryKey: KEYS.properties }),
    rooms:           (propId) => qc.invalidateQueries({ queryKey: propId ? KEYS.rooms(propId) : ["rooms"] }),
    bookings:        () => qc.invalidateQueries({ queryKey: ["bookings"] }),
    tasks:           () => qc.invalidateQueries({ queryKey: ["tasks"] }),
    expenses:        () => qc.invalidateQueries({ queryKey: ["expenses"] }),
    dashboard:       () => qc.invalidateQueries({ queryKey: ["dashboard"] }),
    all:             () => qc.invalidateQueries(),
    // After property changes, also invalidate dependent data
    afterPropertyMutation: () => {
      qc.invalidateQueries({ queryKey: KEYS.properties });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    // After booking changes, invalidate bookings + dashboard
    afterBookingMutation: () => {
      qc.invalidateQueries({ queryKey: ["bookings"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    // After expense changes, invalidate expenses + dashboard
    afterExpenseMutation: () => {
      qc.invalidateQueries({ queryKey: ["expenses"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    },
    // After task changes
    afterTaskMutation: () => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
    },
    // After review changes
    afterReviewMutation: () => {
      qc.invalidateQueries({ queryKey: KEYS.reviews });
      qc.invalidateQueries({ queryKey: ["bookings"] });
    },
    // After inbox/message changes
    messages: () => qc.invalidateQueries({ queryKey: KEYS.messages }),
    // After applying/dismissing a pricing nudge (also refresh properties → calendar rates)
    pricingNudges: () => {
      qc.invalidateQueries({ queryKey: KEYS.pricingNudges });
      qc.invalidateQueries({ queryKey: KEYS.properties });
    },
  };
}
